<template>
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    app
    temporary
    right
    :stateless="isPaymentPanelOpen"
    width="650px"
    class="right-side-panel"
  >
    <panel-header :show.sync="show">
      <div class="request-credit-panel__title">Add Credits to budget entity</div>
    </panel-header>
    <v-divider />
    <cg-loader v-if="loading" />
    <v-form ref="formRequestCredits" @submit.prevent="handleAction">
      <div v-show="!loading" class="request-credit-panel">
        <div class="request-credit-panel__details">
          <!-- BALANCE -->
          <div v-if="!isSuccess || isRequestPaymentMethod" class="request-credit-panel__balance">
            <div class="request-credit-panel__balance-label">Current balance:</div>
            <div
              class="request-credit-panell__balance-value"
              :class="{ 'request-credit-panel__balance-value--is-negative': isBalanceNegative }"
            >
              {{
                creditsBalance
                  | priceFilter({
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                })
              }}
            </div>
          </div>
          <!-- SUCCESS PAGE -->
          <div v-if="isSuccess" class="request-credit-panel__success-page">
            <add-credits-confirmation v-if="isCreditCardPaymentMethod">
              <template #icon>
                <v-img
                  contain
                  max-width="26"
                  max-height="26"
                  width="26"
                  height="26"
                  :src="responseData.approved === false ? '/images/info-icon.svg' : '/images/check-icon-fill.svg'"
                />
              </template>
              <template #text>
                <div>
                  <p class="mb-7">
                    Your payment has been received and your order confirmed!<br/>
                    Your order number is {{ responseData.id }}
                  </p>
                  <p class="mb-0">You have added an additional {{ creditsAmount | priceFilter }} to your balance.</p>
                </div>
              </template>
            </add-credits-confirmation>
            <cg-alert v-if="isRequestPaymentMethod" class="request-credit-panel__success-alert" type="success">
              <div>A request email was sent to {{ selectedAdminName }}.</div>
              <div>You’ll get a notification once balance is added to this budget entity</div>
            </cg-alert>
          </div>
          <!-- FORM -->
          <div v-else class="request-credit-panel__form">
            <!-- CREDIT AMOUNT -->
            <div class="request-credit-panel__field">
              <label class="request-credit-panel__label" for="credits-amount"> Credits amount </label>
              <common-input
                v-model.number="creditsAmount"
                type="number"
                min="1"
                prefix="$"
                id="credits-amount"
                ref="creditsAmount"
                placeholder="0"
                :rules="[(v) => (v && v >= 1) || '']"
                @keydown="blockAmountSeparatorKeydown"
                @paste="blockAmountSeparatorPaste"
              />
            </div>
            <!-- PAYMENT METHOD -->
            <div class="request-credit-panel__field">
              <label class="request-credit-panel__label"> Select method </label>
              <common-select
                v-model="paymentMethod"
                item-text="name"
                item-value="id"
                :items="PAYMENT_OPTIONS"
              />
            </div>
            <!-- SEND REQUEST -->
            <div
              v-if="isRequestPaymentMethod"
              class="request-credit-panel__field request-credit-panel__field--request"
            >
              <label class="request-credit-panel__label"> Send request to super admin: </label>
              <common-select
                v-model="adminSelected"
                item-text="name"
                item-value="id"
                :items="adminOptions"
                class="request-credit-panel__field--select-admins"
                placeholder="Select approver"
                :rules="[(v) => !!v || '']"
              />
              <common-textarea
                v-model="requestText"
                class="request-credit-panel__textarea"
                :rows="10"
                placeholder="We need to increase balance so we’ll be able to add recognition for EOY event"
                :rules="[(v) => !!v || '']"
              />
            </div>
            <!-- PAYMENT MODULE -->
            <div
              v-if="show && isCreditCardPaymentMethod"
              class="request-credit-panel__field request-credit-panel__field--payment"
            >
              <label class="request-credit-panel__label"> Choose a payment method: </label>
              <payment-module
                :paymentMethod.sync="payment.method"
                :paymentData.sync="payment.paymentData"
                :amount="creditsAmount"
                :availableMethods="availablePaymentMethods"
                ref="paymentModule"
                @update:isPaymentPanelOpen="(val) => isPaymentPanelOpen = val"
              />
            </div>
          </div>
        </div>
        <!-- ACTIONS -->
        <div class="request-credit-panel__actions">
          <cg-button
            class="request-credit-panel__save-button"
            @click="handleAction"
            :disabled="loading"
            type="button"
          >
            {{ actionButtonText }}
          </cg-button>
          <!-- TERMS -->
          <div v-if="isCreditCardPaymentMethod && !isSuccess" class="request-credit-panel__terms">
            By clicking “Buy credits” you agree to the CorporateGift.com
            <a
              href="https://corporategift.com/terms-and-conditions/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service and Payments Policy.
            </a>
          </div>
        </div>
      </div>
    </v-form>
  </v-navigation-drawer>
</template>

<script>
import Api from '@/axios/api';

import panelVModel from '@/mixins/panelVModel';
import { selectedEntity } from '../mixins';
import PaymentType from '@/components/payment/paymentModule/utils/PaymentType';
import getPaymentBody from '@/components/payment/paymentModule/utils/getPaymentBody';

import { CgButton, CgLoader, CgAlert } from '@corporategiftcom/design-system';
import PanelHeader from '@/components/PanelHeader.vue';
import PaymentModule from '@/components/payment/PaymentModule.vue';

import { PAYMENT_OPTION_IDS, PAYMENT_OPTIONS } from '@/constants/paymentOptions';
import {
  creditsPurchase,
  creditsSelectPaymentMethod,
  destinationDeclined,
} from '@/plugins/googleTagManager';
import AddCreditsConfirmation from "@/components/myCGCredits/credits/AddCreditsConfirmation.vue";
import blockAmountSeparatorKeydown, { blockAmountSeparatorPaste } from '@/utils/blockAmountSeparatorKeydown';

export default {
  name: 'TheRequestCreditsPanel',
  mixins: [panelVModel, selectedEntity],
  components: {
    AddCreditsConfirmation,
    CgLoader,
    CgButton,
    CgAlert,
    PanelHeader,
    PaymentModule,
  },
  data: () => ({
    loading: false,
    creditsAmount: null,
    payment: {
      method: PaymentType.CC,
      paymentData: null,
    },
    isPaymentPanelOpen: false,
    availablePaymentMethods: [PaymentType.CC, PaymentType.ACH, PaymentType.PO],
    responseData: {},
    adminOptions: [],
    paymentMethod: PAYMENT_OPTION_IDS.CREDIT_CARD,
    requestText: '',
    adminSelected: null,
    isSuccess: false,
    PAYMENT_OPTIONS,
    PAYMENT_OPTION_IDS,
  }),
  computed: {
    actionButtonText() {
      if (this.isSuccess) {
        return 'Close';
      }

      if (this.paymentMethod === PAYMENT_OPTION_IDS.REQUEST) {
        return 'Send request';
      }

      return 'Buy credits';
    },
    isCloseAction() {
      return this.isSuccess;
    },
    isCreditCardPaymentMethod() {
      return this.paymentMethod === PAYMENT_OPTION_IDS.CREDIT_CARD;
    },
    isRequestPaymentMethod() {
      return this.paymentMethod === PAYMENT_OPTION_IDS.REQUEST;
    },
    creditsBalance() {
      return this.selectedEntity?.balance ?? 0;
    },
    isBalanceNegative() {
      return this.creditsBalance < 0;
    },
    selectedAdminName() {
      const admin = this.adminOptions.find(a => a.id === this.adminSelected);
      return admin ? admin.name : '';
    }
  },
  watch: {
    show: function (val) {
      if (val) {
        this.fetchData();
      } else {
        // reset data and validation
        this.$refs.formRequestCredits.resetValidation();
        Object.assign(this.$data, this.$options.data());
      }
    },
    googleAnalyticsPaymentMethod: function (val) {
      if (val) {
        creditsSelectPaymentMethod({ paymentType: val });
      }
    },
  },
  methods: {
    blockAmountSeparatorKeydown,
    blockAmountSeparatorPaste,
    googleAnalyticsPaymentMethod() {
      const { method } = this.payment;
      let GAPaymentMethod = method;

      if (method === PaymentType.CC) {
        GAPaymentMethod = 'credit card';
      }
      if (method === PaymentType.ACH) {
        GAPaymentMethod = 'ACH';
      }
      if (method === PaymentType.PO) {
        GAPaymentMethod = 'purchase order';
      }

      return GAPaymentMethod;
    },
    handleAction() {
      if (this.isCloseAction) {
        this.show = false;
        return;
      }

      if (!this.$refs.formRequestCredits.validate()) {
        return;
      }

      this.submitAction();
    },
    handleApiError(error) {
      const errorMessage = error?.response?.data?.message;

      this.$cgToast.error(errorMessage || 'An error occurred, please contact our support', {
        html: true,
      });
    },
    async submitAction() {
      this.loading = true;

      try {
        const success = this.isRequestPaymentMethod
          ? await this.postSendRequest()
          : await this.postBuyCredits();

        if (success) {
          this.isSuccess = true;
          this.$emit('fetchNewData');
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchData() {
      this.loading = true;

      try {
        await this.getApprovers();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.loading = false;
      }
    },
    async getApprovers() {
      const { data } = await Api.get('/credit/reload-requests/approvers');
      this.adminOptions = data;
    },
    async postSendRequest() {
      try {
        const { id } = this.selectedEntity;

        const payload = {
          description: this.requestText,
          budget_entity_id: id,
          approver_id: this.adminSelected,
          amount: this.creditsAmount,
        };

        const response = await Api.post('/credit/reload-requests', payload);

        return response?.success ?? false;
      } catch (error) {
        this.handleApiError(error);
        return false;
      }
    },
    async postBuyCredits() {
      const { creditsAmount, payment } = this;
      const { id } = this.selectedEntity;

      const isCreditsAmountValid = this.$refs.creditsAmount.validate(true);
      const isPaymentMethodValid = this.$refs.paymentModule.validate();

      if (!isCreditsAmountValid || !isPaymentMethodValid) return false;

      try {
        const paymentBody = getPaymentBody(payment);

        const requestBody = {
          amount: creditsAmount,
          payment: [
            {
              ...paymentBody,
              budget_entity_id: id,
            },
          ],
        };

        const response = await Api.post('/credit', requestBody);
        this.responseData = response;

        if (!response?.success) {
          if (response?.message) {
            destinationDeclined({ action: response.message });
            this.$cgToast.error(response.message, { html: true });
          }
          return false;
        }

        // analytics
        creditsPurchase({
          paymentType: this.googleAnalyticsPaymentMethod,
          value: creditsAmount,
        });

        // side-effects
        if (response?.approved) {
          this.$emit('updateAmount', creditsAmount);
        }

        return true;
      } catch (error) {
        this.handleApiError(error);
        return false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/common/right-side-panel.scss';

.request-credit-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__details {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__success-alert {
    padding: 16px 20px;

    &::v-deep > .cg-alert__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      flex: 1 0 0;

      > div {
        color: #222325;
        font-size: 15px;

        &:first-child {
          font-weight: 700;
          line-height: normal;
        }

        &:last-child {
          font-weight: 400;
          line-height: 143.5%;
        }
      }
    }
  }

  /* BALANCE */
  &__balance {
    display: flex;
    flex-direction: column;
  }

  &__balance-label {
    color: #000;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
  }

  &__balance-value {
    color: #000;
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: 44.5px;

    &--is-negative {
      color: #ff5a60;
    }
  }

  /* FORM */
  &__form {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;

    &--request {
      flex-basis: 100%;
      margin-top: 24px;
    }

    &--select-admins {
      width: 49%;
    }

    &--payment {
      flex-basis: 100%;
      margin-top: 24px;
    }

    .common-textarea {
      margin-top: 14px;
    }
  }

  &__label {
    color: #222325;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  &__textarea {
    resize: vertical;
  }

  /* ACTIONS */
  &__actions {
    display: flex;
    justify-content: flex-start;
    gap: 16px;
    flex-direction: column;
  }

  &__save-button {
    max-width: 200px;
    border-radius: 100px;
  }

  /* TERMS */
  &__terms {
    color: #62646a;
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;

    a {
      color: #6e50ff;

      &:hover {
        text-decoration: none;
      }
    }
  }
}
</style>
